import React, { useMemo, useRef, useEffect, useState } from 'react';
import { Tooltip } from 'antd';
import {
  FolderOpenOutlined,
  FileTextOutlined,
  AuditOutlined,
  DollarOutlined,
  BankOutlined,
  GoldOutlined,
  SearchOutlined,
  SafetyOutlined,
  FileDoneOutlined,
  CheckCircleOutlined,
  ForwardOutlined,
} from '@ant-design/icons';

/* ────────────────────────────────────────────────────────
   Step definitions — order = pipeline order
   ──────────────────────────────────────────────────────── */
const STEP_DEFS = [
  { key: 'CASE_CREATED',        label: 'Case Created',          icon: FolderOpenOutlined,  optional: false },
  { key: 'LOAN_DOCUMENTS',      label: 'Loan Documents',        icon: FileTextOutlined,    optional: true  },
  { key: 'PRE_LOAN_AGREEMENT',  label: 'Pre-Loan Agreement',    icon: AuditOutlined,       optional: false },
  { key: 'ADVANCE_PAYMENT',     label: 'Advance Payment',       icon: DollarOutlined,      optional: false },
  { key: 'LOAN_CLEARANCE',      label: 'Loan Clearance',        icon: BankOutlined,        optional: false },
  { key: 'GOLD_RECEIVED',       label: 'Gold Received',         icon: GoldOutlined,        optional: false },
  { key: 'VALUATION_COMPLETED', label: 'Valuation',             icon: SearchOutlined,      optional: false },
  { key: 'FINAL_AGREEMENT',     label: 'Final Agreement',       icon: SafetyOutlined,      optional: false },
  // Settlement comes BEFORE the bill: the customer is paid first, then the bill
  // is raised carrying the full split-payment trail.
  { key: 'SETTLEMENT_CLOSURE',  label: 'Settlement',            icon: CheckCircleOutlined, optional: false },
  { key: 'BILL_GENERATED',      label: 'Bill Generated',        icon: FileDoneOutlined,    optional: false },
];

/* Map backend status → step index that becomes "active" for that status */
const STATUS_TO_ACTIVE_INDEX = {
  LEAD_IDENTIFIED:              0,
  DOCS_VERIFIED:                2,   // docs done  → pre-loan agreement is active
  ADVANCE_APPROVED:             3,   // agreement signed → advance payment
  PART_PAYMENT_DONE:            4,   // advances done → loan clearance
  FULLY_PAID_AWAITING_RELEASE:  5,   // loan cleared → gold received
  GOLD_RECEIVED:                6,   // gold in hand → valuation
  VALUATION_COMPLETED:          7,   // valuation done → final agreement, then settlement
  PAYMENT_SETTLED:              9,   // customer paid in full → bill is the last step
  BILL_GENERATED:               9,   // legacy (bill-first) cases
  CLOSED:                       10,  // past the last step — everything complete
};

/* ────────────────────────────────────────────────────────
   Arrow-style colour palettes per status
   ──────────────────────────────────────────────────────── */
const ARROW_PALETTE = {
  completed: {
    bg: '#2f855a',
    flat: '#2f855a',
    text: '#fff',
    icon: '#fff',
    border: 'rgba(56,249,215,0.3)',
    shadow: '0 2px 8px rgba(67,233,123,0.25)',
    labelColor: '#15803d',
  },
  active: {
    bg: '#2563eb',
    flat: '#2563eb',
    text: '#fff',
    icon: '#fff',
    border: 'rgba(118,75,162,0.4)',
    shadow: '0 4px 16px rgba(102,126,234,0.35)',
    labelColor: '#5b21b6',
  },
  skipped: {
    bg: '#d97706',
    flat: '#d97706',
    text: '#fff',
    icon: '#fff',
    border: 'rgba(253,160,133,0.3)',
    shadow: '0 2px 8px rgba(246,211,101,0.25)',
    labelColor: '#b45309',
  },
  pending: {
    bg: '#e2e8f0',
    flat: '#e2e8f0',
    text: '#94a3b8',
    icon: '#94a3b8',
    border: 'rgba(203,213,225,0.5)',
    shadow: 'none',
    labelColor: '#94a3b8',
  },
};

/* ────────────────────────────────────────────────────────
   Derive step statuses from case data
   ──────────────────────────────────────────────────────── */
export function deriveStepStatuses(caseData, goldReceipt, agreements) {
  const status = caseData?.status || 'LEAD_IDENTIFIED';
  let activeIdx = STATUS_TO_ACTIVE_INDEX[status] ?? 0;

  const hasDocs = (caseData?.documents || []).length > 0;
  const hasPreLoan = (agreements || []).some(a => a.agreementType === 'PRE_LOAN_TAKEOVER');
  const hasAdvance = (caseData?.totalAdvancePaid || 0) > 0 || (caseData?.totalLoanPaid || 0) > 0;
  const hasGoldReceipt = !!goldReceipt;
  const hasValuation = hasGoldReceipt && !!goldReceipt.valuationDate;
  const hasFinalAgreement = (agreements || []).some(a => a.agreementType === 'FINAL_PURCHASE');
  const hasBill = !!caseData?.billId;
  const isClosed = status === 'CLOSED';
  // The customer has been paid everything they were owed. Under the new order
  // this is what unlocks billing; a legacy (bill-first) closed case counts too.
  const settlementAmount = caseData?.finalSettlementAmount || 0;
  const remaining = settlementAmount - (caseData?.totalSettlementPaid || 0);
  const isFullySettled = status === 'PAYMENT_SETTLED'
    || isClosed
    || (!!caseData?.settlementFinalizedAt && remaining <= 0.5);

  // Auto-advance activeIdx based on frontend reality if backend status lags behind
  if (activeIdx <= 1 && caseData?.documentVerification?.status === 'APPROVED') {
    activeIdx = 2; // Move to PRE_LOAN_AGREEMENT
  }
  if (activeIdx <= 2 && hasPreLoan) {
    activeIdx = 3; // Move to ADVANCE_PAYMENT
  }
  if (activeIdx <= 7 && hasFinalAgreement) {
    activeIdx = 8; // Move to BILL_GENERATED
  }

  return STEP_DEFS.map((step, idx) => {
    if (step.key === 'CASE_CREATED') {
      return { ...step, status: 'completed' };
    }
    if (step.key === 'LOAN_DOCUMENTS') {
      if (hasDocs) return { ...step, status: activeIdx > idx ? 'completed' : 'active' };
      if (activeIdx > idx) return { ...step, status: 'skipped' };
      if (activeIdx === idx) return { ...step, status: 'active' };
      return { ...step, status: 'pending' };
    }
    if (step.key === 'PRE_LOAN_AGREEMENT') {
      if (hasPreLoan) return { ...step, status: activeIdx > idx ? 'completed' : 'active' };
      if (activeIdx > idx) return { ...step, status: 'completed' };
      if (activeIdx === idx) return { ...step, status: 'active' };
      return { ...step, status: 'pending' };
    }
    if (step.key === 'ADVANCE_PAYMENT') {
      if (hasAdvance && activeIdx > idx) return { ...step, status: 'completed' };
      if (activeIdx >= idx && hasAdvance) return { ...step, status: 'completed' };
      if (activeIdx === idx) return { ...step, status: 'active' };
      if (activeIdx > idx) return { ...step, status: 'completed' };
      return { ...step, status: 'pending' };
    }
    if (step.key === 'LOAN_CLEARANCE') {
      if (activeIdx > idx) return { ...step, status: 'completed' };
      if (activeIdx === idx) return { ...step, status: 'active' };
      return { ...step, status: 'pending' };
    }
    if (step.key === 'GOLD_RECEIVED') {
      if (activeIdx > idx) return { ...step, status: 'completed' };
      if (activeIdx === idx) return { ...step, status: 'active' };
      return { ...step, status: 'pending' };
    }
    if (step.key === 'VALUATION_COMPLETED') {
      if (hasValuation && activeIdx >= idx) return { ...step, status: 'completed' };
      if (activeIdx > idx) return { ...step, status: 'completed' };
      if (activeIdx === idx) return { ...step, status: 'active' };
      return { ...step, status: 'pending' };
    }
    if (step.key === 'FINAL_AGREEMENT') {
      if (hasFinalAgreement) return { ...step, status: activeIdx > idx ? 'completed' : 'active' };
      if (activeIdx > idx) return { ...step, status: 'completed' };
      if (activeIdx === idx) return { ...step, status: 'active' };
      return { ...step, status: 'pending' };
    }
    if (step.key === 'SETTLEMENT_CLOSURE') {
      // Done once the customer has been paid in full; active while the final
      // agreement is signed and payouts are in progress.
      if (isFullySettled) return { ...step, status: 'completed' };
      if (activeIdx >= idx || hasFinalAgreement) return { ...step, status: 'active' };
      return { ...step, status: 'pending' };
    }
    if (step.key === 'BILL_GENERATED') {
      // The last step — the bill is raised against a completed settlement.
      if (hasBill) return { ...step, status: 'completed' };
      if (isFullySettled) return { ...step, status: 'active' };
      return { ...step, status: 'pending' };
    }
    // Fallback
    if (activeIdx > idx) return { ...step, status: 'completed' };
    if (activeIdx === idx) return { ...step, status: 'active' };
    return { ...step, status: 'pending' };
  });
}

/* ────────────────────────────────────────────────────────
   SVG Arrow Chevron – one segment of the progress bar
   ──────────────────────────────────────────────────────── */
const ARROW_H = 54;               // total arrow height
const ARROW_W = 130;              // body width
const NOTCH  = 16;                // depth of the arrow point / notch
const ARROW_TOTAL_W = ARROW_W + NOTCH;

const ArrowSVG = ({ status, isFirst, isLast }) => {
  const pal = ARROW_PALETTE[status] || ARROW_PALETTE.pending;

  // Build the chevron path
  // First arrow has a flat left edge; last arrow has a flat right edge
  const leftNotch = isFirst ? 0 : NOTCH;
  const rightPointX = isLast ? ARROW_W + NOTCH : ARROW_W + NOTCH;

  const path = isFirst
    ? `M 0,0 L ${ARROW_W},0 L ${rightPointX},${ARROW_H / 2} L ${ARROW_W},${ARROW_H} L 0,${ARROW_H} Z`
    : `M 0,0 L ${ARROW_W},0 L ${rightPointX},${ARROW_H / 2} L ${ARROW_W},${ARROW_H} L 0,${ARROW_H} L ${leftNotch},${ARROW_H / 2} Z`;

  return (
    <svg
      width={ARROW_TOTAL_W}
      height={ARROW_H}
      viewBox={`0 0 ${ARROW_TOTAL_W} ${ARROW_H}`}
      style={{ display: 'block' }}
    >
      <path d={path} fill={pal.bg} />
      {status === 'active' && (
        <path d={path} fill="rgba(255,255,255,0.08)" />
      )}
    </svg>
  );
};

/* ────────────────────────────────────────────────────────
   Individual Arrow Step
   ──────────────────────────────────────────────────────── */
const ArrowStep = ({ step, index, total, onClick, isClickable }) => {
  const pal = ARROW_PALETTE[step.status] || ARROW_PALETTE.pending;
  const Icon = step.icon;
  const isFirst = index === 0;
  const isLast = index === total - 1;
  const [hovered, setHovered] = useState(false);

  const tooltipText = (() => {
    if (step.status === 'skipped') return `${step.label} — skipped (optional)`;
    if (step.status === 'completed') return `${step.label} — completed ✓`;
    if (step.status === 'active') return `${step.label} — in progress`;
    return `${step.label} — pending`;
  })();

  const statusIcon = (() => {
    if (step.status === 'completed') return <CheckCircleOutlined style={{ fontSize: 13, color: pal.icon }} />;
    if (step.status === 'skipped') return <ForwardOutlined style={{ fontSize: 12, color: pal.icon }} />;
    return <Icon style={{ fontSize: 14, color: pal.icon }} />;
  })();

  return (
    <Tooltip title={tooltipText} placement="bottom" mouseEnterDelay={0.25}>
      <div
        onClick={isClickable ? () => onClick(step.key, index) : undefined}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
        className="arrow-step-wrapper"
        style={{
          position: 'relative',
          cursor: isClickable ? 'pointer' : 'default',
          marginLeft: index === 0 ? 0 : -NOTCH + 1,
          zIndex: total - index,
          transition: 'transform 0.2s ease, filter 0.2s ease',
          transform: hovered && isClickable ? 'translateY(-2px)' : 'translateY(0)',
          filter: hovered && isClickable
            ? 'brightness(1.1) drop-shadow(0 4px 12px rgba(0,0,0,0.15))'
            : step.status === 'pending' ? 'saturate(0.6)' : 'none',
        }}
      >
        {/* SVG Arrow background */}
        <ArrowSVG status={step.status} isFirst={isFirst} isLast={isLast} />

        {/* Content overlay on top of the arrow */}
        <div
          style={{
            position: 'absolute',
            top: 0,
            left: isFirst ? 0 : NOTCH,
            right: 0,
            bottom: 0,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 6,
            paddingLeft: 4,
            paddingRight: NOTCH + 4,
          }}
        >
          {/* Step number badge */}
          <div
            style={{
              width: 22,
              height: 22,
              borderRadius: '50%',
              background: 'rgba(255,255,255,0.25)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              flexShrink: 0,
            }}
          >
            {statusIcon}
          </div>

          {/* Label */}
          <span
            style={{
              fontSize: 11,
              fontWeight: step.status === 'active' ? 700 : 600,
              color: pal.text,
              whiteSpace: 'nowrap',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: '13px',
              letterSpacing: '0.01em',
              textShadow: step.status !== 'pending' ? '0 1px 2px rgba(0,0,0,0.15)' : 'none',
            }}
          >
            {step.label}
          </span>
        </div>

        {/* Active pulse ring */}
        {step.status === 'active' && (
          <div
            className="active-pulse"
            style={{
              position: 'absolute',
              top: -3,
              left: isFirst ? -3 : NOTCH - 3,
              right: -3,
              bottom: -3,
              border: '2px solid rgba(102,126,234,0.35)',
              borderRadius: 6,
              pointerEvents: 'none',
            }}
          />
        )}
      </div>
    </Tooltip>
  );
};

/* ────────────────────────────────────────────────────────
   Main CaseProgressStepper
   ──────────────────────────────────────────────────────── */
const CaseProgressStepper = ({ caseData, goldReceipt, agreements, activeTab, onStepClick }) => {
  const steps = useMemo(
    () => deriveStepStatuses(caseData, goldReceipt, agreements),
    [caseData, goldReceipt, agreements],
  );

  const scrollRef = useRef(null);
  const [mounted, setMounted] = useState(false);
  useEffect(() => { setMounted(true); }, []);
  useEffect(() => {
    if (!mounted || !scrollRef.current) return;
    const activeEl = scrollRef.current.querySelector('[data-step-active="true"]');
    if (activeEl) {
      activeEl.scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
    }
  }, [steps, mounted]);

  const handleClick = (key) => {
    if (onStepClick) onStepClick(key);
  };

  return (
    <div
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 100,
        background: '#ffffff',
        borderBottom: '1px solid #e2e8f0',
        boxShadow: '0 4px 20px rgba(0,0,0,0.06)',
        padding: '14px 16px 12px',
      }}
    >
      {/* Progress percentage */}
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 10,
        paddingLeft: 4,
        paddingRight: 4,
      }}>
        <span style={{ fontSize: 12, fontWeight: 600, color: '#475569', letterSpacing: '0.03em' }}>
          CASE PROGRESS
        </span>
        <span style={{
          fontSize: 12,
          fontWeight: 700,
          color: '#667eea',
          background: 'rgba(102,126,234,0.1)',
          padding: '2px 10px',
          borderRadius: 12,
        }}>
          {Math.round(steps.filter(s => s.status === 'completed' || s.status === 'skipped').length / steps.length * 100)}%
        </span>
      </div>

      {/* Thin overall progress bar */}
      <div style={{
        height: 3,
        background: '#e2e8f0',
        borderRadius: 4,
        marginBottom: 12,
        overflow: 'hidden',
      }}>
        <div style={{
          height: '100%',
          width: `${steps.filter(s => s.status === 'completed' || s.status === 'skipped').length / steps.length * 100}%`,
          background: '#2563eb',
          borderRadius: 4,
          transition: 'width 0.6s cubic-bezier(0.34, 1.56, 0.64, 1)',
        }} />
      </div>

      {/* Arrow steps */}
      <div
        ref={scrollRef}
        style={{
          display: 'flex',
          alignItems: 'center',
          overflowX: 'auto',
          WebkitOverflowScrolling: 'touch',
          scrollbarWidth: 'none',
          msOverflowStyle: 'none',
          paddingBottom: 4,
        }}
      >
        {steps.map((step, idx) => {
          const isClickable = step.status === 'completed' || step.status === 'active' || step.status === 'skipped';
          return (
            <div
              key={step.key}
              data-step-active={step.status === 'active' ? 'true' : 'false'}
            >
              <ArrowStep
                step={step}
                index={idx}
                total={steps.length}
                onClick={handleClick}
                isClickable={isClickable}
              />
            </div>
          );
        })}
      </div>

      {/* Keyframes & scrollbar hide */}
      <style>{`
        div[style*="overflowX: auto"]::-webkit-scrollbar { display: none; }
        @keyframes pulse-ring {
          0%   { opacity: 0.6; transform: scale(1); }
          50%  { opacity: 0.2; transform: scale(1.02); }
          100% { opacity: 0.6; transform: scale(1); }
        }
        .active-pulse {
          animation: pulse-ring 2s ease-in-out infinite;
        }
        .arrow-step-wrapper:active {
          transform: translateY(0) scale(0.98) !important;
        }
      `}</style>
    </div>
  );
};

/* ────────────────────────────────────────────────────────
   Export step key → tab key mapping
   ──────────────────────────────────────────────────────── */
export const STEP_KEY_TO_TAB = {
  CASE_CREATED:        'summary',
  LOAN_DOCUMENTS:      'documents',
  PRE_LOAN_AGREEMENT:  'pre-loan-agreement',
  ADVANCE_PAYMENT:     'advances',
  LOAN_CLEARANCE:      'advances',     // same tab, different section
  GOLD_RECEIVED:       'gold-receipt',
  VALUATION_COMPLETED: 'gold-receipt',
  FINAL_AGREEMENT:     'final-agreement',
  BILL_GENERATED:      'bill',
  SETTLEMENT_CLOSURE:  'settlement',
};

export default CaseProgressStepper;
